import { getVdsSpacing } from '@cas/utils/vdsHelpers';
import {
  ChartColorScheme,
  schemeBlue,
  schemeGrey,
  schemeOrangeGrey,
  schemeRedGrey,
  schemeRedOrangeGrey,
  schemeRedOrangeYellow,
  schemeRedYellowGrey,
  schemeYellowGrey,
} from './config';
import { TFunction } from 'i18next';
import {
  Chart,
  ChartOptions,
  DefaultDataPoint,
  FontSpec,
  LegendItem,
  LegendOptions,
  LinearScaleOptions,
  Scale,
  ScriptableScaleContext,
  TooltipItem,
  TooltipLabelStyle,
} from 'chart.js';
import { DateTime, DateTimeUnit, Duration, DurationLikeObject, Interval } from 'luxon';
import { CUSTOM_RANGE_PLUGIN_ID, customRangeOptionsLight } from './custom-range/CustomRangePlugin';
import { CustomTimeRangeParams } from '@customer/store/apis/helpers';
import { TimeUnit } from '@customer/store/apis/scenariosApi';
import {
  BarSelectPluginId,
  CustomBarChartDataset,
  CustomBarChartLabel,
  OnBarSelect,
  OnCategorySelect,
  OnGetValue,
} from './bar-select/BarSelectPlugin';
import { ThemeColors } from '@volt/volt-basic-components/dist/types/definitions/colors';

/**
 * The DeepPartial type is used by Chart.js but not exported.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type DeepPartial<T> = T extends Function
  ? T
  : T extends Array<infer U>
    ? _DeepPartialArray<U>
    : T extends object
      ? _DeepPartialObject<T>
      : T | undefined;

type _DeepPartialArray<T> = Array<DeepPartial<T>>;
type _DeepPartialObject<T> = { [P in keyof T]?: DeepPartial<T[P]> };

// The chart-specific callback might require the index or the category (though category isn't currently used for charts yet).
// It permits the tooltip to be customised for either the entire (stacked) bar, or for specific categories within a stacked bar.
export type GetTooltipTitle = (
  index: number,
  categoryLabelKey: string,
  datasetLabelKey: string,
) => string;

const LEGEND_HEIGHT = 40;
export const BAR_THICKNESS = getVdsSpacing(6);
const BAR_GAP = getVdsSpacing(5);
const BAR_SECTION_THICKNESS = BAR_THICKNESS + BAR_GAP;
const TICK_PADDING = getVdsSpacing(5);
const X_AXIS_TICK_HEIGHT = 20;
const X_AXIS_HEIGHT = X_AXIS_TICK_HEIGHT + TICK_PADDING * 2;
const HISTOGRAM_BAR_HEIGHT = 266;
const HISTOGRAM_CHART_HEIGHT = HISTOGRAM_BAR_HEIGHT + X_AXIS_HEIGHT + LEGEND_HEIGHT;

export const getColorScheme = (colorScheme: ChartColorScheme) => {
  switch (colorScheme) {
    case ChartColorScheme.RedGrey:
      return schemeRedGrey;
    case ChartColorScheme.OrangeGrey:
      return schemeOrangeGrey;
    case ChartColorScheme.YellowGrey:
      return schemeYellowGrey;
    case ChartColorScheme.RedYellowGrey:
      return schemeRedYellowGrey;
    case ChartColorScheme.RedOrangeGrey:
      return schemeRedOrangeGrey;
    case ChartColorScheme.RedOrangeYellow:
      return schemeRedOrangeYellow;
    case ChartColorScheme.Blue:
      return schemeBlue;
    case ChartColorScheme.Custom:
    default:
      return schemeGrey;
  }
};

// Convert VDS color token names to specific hex values based on the theme mode (light/dark).
const getThemeColor = (darkTheme: boolean, tokenName: ThemeColors): string => {
  switch (tokenName) {
    case 'color-text-white-high':
      return darkTheme ? '#041821' : '#ffffff';
    case 'color-text-medium':
      return darkTheme ? '#FFFFFFA3' : '#041821A3';
    case 'color-text-high':
      return darkTheme ? '#FFFFFF' : '#041821';
    case 'color-border-lower':
      return darkTheme ? '#FFFFFF29' : '#04182129';
    case 'surface-overlay-default':
      return darkTheme ? '#ffffff' : '#213142';
    default:
      return darkTheme ? 'white' : 'black';
  }
};

function hideFirstAxisLine(context: ScriptableScaleContext, darkTheme: boolean) {
  if (context.tick.value > 0) {
    return getThemeColor(darkTheme, 'color-border-lower');
  } else {
    return 'transparent';
  }
}

const barChartTitleCallbackFactory = (getTooltipTitle: GetTooltipTitle) => {
  return function (context: CustomTooltip[]): string {
    const {
      dataIndex,
      chart,
      dataset: { labelSource },
    } = context[0];

    // Get the orientation of the bar chart - default to 'x'.
    const indexAxis = chart.config.options?.indexAxis ?? 'x';

    // Look up the category label object on the index axis scale.
    // @ts-expect-error: The custom label object is not being recognised as the return value by the typescript compiler.
    const category: CustomBarChartLabel = chart.scales[indexAxis].getLabelForValue(dataIndex);

    return getTooltipTitle(dataIndex, category.name, labelSource);
  };
};

const barChartLabelCallbackFactory = (t: TFunction, transKey: string, numberLocale: string) => {
  return function (context: CustomTooltip): string {
    const dataValue: number | [number, number] | null = context.dataset.data[context.dataIndex];
    const count = typeof dataValue === 'number' ? dataValue : 0;
    // Note: The space at the front of the return string is intentional (for display padding).
    return ` ${t(transKey, { count, formatParams: { count: { lng: numberLocale } } })}`;
  };
};

function barChartTicksCallback(this: Scale, value: string | number): string {
  // 'For a category axis, the val is the index so the lookup via getLabelForValue is needed.'
  // See: https://www.chartjs.org/docs/latest/samples/scale-options/ticks.html
  const tickIndex = typeof value === 'string' ? parseInt(value) : value;

  // @ts-expect-error: The custom label object is not being recognised as the return value by the typescript compiler.
  const customLabel: CustomBarChartLabel = this.getLabelForValue(tickIndex);
  return customLabel.label;
}

interface CustomTooltip extends TooltipItem<'bar'> {
  chart: Chart<'bar', DefaultDataPoint<'bar'>, CustomBarChartLabel>;
  dataset: CustomBarChartDataset;
}

// There is no way to specify a custom aspect ratio, but these settings are necessary to allow responsive behaviour.
const responsiveOptions = { maintainAspectRatio: false, responsive: true };

const titleOptions = { display: false };

// Except for the font family, these options are the same as the FontSpec default values.
const defaultFont: FontSpec = {
  family: '"Roboto Flex", sans-serif',
  size: 12,
  weight: 400,
  lineHeight: 1.2,
  style: 'normal',
};

const tooltipTitleFont: FontSpec = {
  ...defaultFont,
  size: 14,
  weight: 600,
  lineHeight: 1.25,
};

const tooltipBodyFont: FontSpec = {
  ...defaultFont,
  size: 14,
  weight: 300,
  lineHeight: 1.25,
};

const categoryLabelFont: FontSpec = { ...defaultFont, size: 14, weight: 300 };

const getTooltipOptions = (darkTheme: boolean) => ({
  enabled: true,
  backgroundColor: getThemeColor(darkTheme, 'surface-overlay-default'),
  titleColor: getThemeColor(darkTheme, 'color-text-white-high'),
  titleFont: tooltipTitleFont,
  bodyColor: getThemeColor(darkTheme, 'color-text-white-high'),
  bodyFont: tooltipBodyFont,
  padding: getVdsSpacing(4),
});

const getBarChartTooltipOptions = (
  darkTheme: boolean,
  t: TFunction,
  transKey: string,
  numberLocale: string,
  getTooltipTitle: GetTooltipTitle,
) => {
  return {
    ...getTooltipOptions(darkTheme),
    callbacks: {
      title: barChartTitleCallbackFactory(getTooltipTitle),
      label: barChartLabelCallbackFactory(t, transKey, numberLocale),
    },
  };
};

const legendOptions = (
  darkTheme: boolean,
  generateLabels?: (chart: Chart) => LegendItem[],
): DeepPartial<LegendOptions<'line' | 'bar'>> => {
  const labels: DeepPartial<LegendOptions<'line' | 'bar'>>['labels'] = {
    color: getThemeColor(darkTheme, 'color-text-medium'),
    boxWidth: 20,
    boxHeight: 8,
    useBorderRadius: true,
    borderRadius: 4,
    padding: getVdsSpacing(5),
    font: defaultFont,
    generateLabels,
  };

  return {
    display: true,
    position: 'bottom',
    align: 'center',
    maxHeight: LEGEND_HEIGHT,
    labels,
  };
};

const commmonBarChartAxisOptions: DeepPartial<LinearScaleOptions> = {
  stacked: true,
  beginAtZero: true,
  border: { display: false },
};

const getChartOptionsBar = (
  darkTheme: boolean,
  t: TFunction,
  transKey: string,
  numberLocale: string,
  getTooltipTitle: GetTooltipTitle,
  getNumberText: OnGetValue,
  onCategorySelect?: OnCategorySelect,
  onBarSelect?: OnBarSelect,
): ChartOptions<'bar'> => ({
  ...responsiveOptions,
  indexAxis: 'y',
  plugins: {
    title: titleOptions,
    tooltip: getBarChartTooltipOptions(darkTheme, t, transKey, numberLocale, getTooltipTitle),
    legend: legendOptions(darkTheme),
    // Enable the bar select plugin if either of the callbacks are provided.
    [BarSelectPluginId]:
      onCategorySelect || onBarSelect
        ? {
            onCategorySelect,
            onBarSelect,
          }
        : false,
  },
  layout: {
    padding: 0,
  },
  scales: {
    x: {
      ...commmonBarChartAxisOptions,
      grid: {
        display: true,
        z: 1,
        drawTicks: false, // Hides the line that extends down below the axis line.
        color: (ctx: ScriptableScaleContext) => hideFirstAxisLine(ctx, darkTheme),
      },
      ticks: {
        display: true,
        padding: TICK_PADDING,
        color: getThemeColor(darkTheme, 'color-text-medium'),
        font: defaultFont,
        precision: 0,
        callback: getNumberText,
      },
    },
    y: {
      ...commmonBarChartAxisOptions,
      grid: {
        display: false,
        drawTicks: true, // Empty space between labels and bars.
      },
      ticks: {
        display: true,
        padding: TICK_PADDING,
        color: getThemeColor(darkTheme, 'color-text-high'),
        font: categoryLabelFont,
        callback: barChartTicksCallback,
      },
    },
  },
});

const getChartOptionsHistogram = (
  darkTheme: boolean,
  t: TFunction,
  transKey: string,
  numberLocale: string,
  getTooltipTitle: GetTooltipTitle,
  getNumberText: OnGetValue,
  onCategorySelect?: OnCategorySelect,
  onBarSelect?: OnBarSelect,
): ChartOptions<'bar'> => ({
  ...responsiveOptions,
  plugins: {
    title: titleOptions,
    tooltip: getBarChartTooltipOptions(darkTheme, t, transKey, numberLocale, getTooltipTitle),
    legend: legendOptions(darkTheme),
    // Enable the bar select plugin if either of the callbacks are provided.
    [BarSelectPluginId]:
      onCategorySelect || onBarSelect
        ? {
            onCategorySelect,
            onBarSelect,
          }
        : false,
  },
  layout: {
    padding: 0,
  },
  scales: {
    x: {
      ...commmonBarChartAxisOptions,
      grid: {
        display: false,
        drawTicks: false, // hides the line that extends down below the axis line
      },
      ticks: {
        display: true,
        padding: TICK_PADDING,
        color: getThemeColor(darkTheme, 'color-text-medium'),
        font: defaultFont,
        precision: 0,
        callback: barChartTicksCallback,
      },
    },
    y: {
      ...commmonBarChartAxisOptions,
      grid: {
        display: true,
        z: 1,
        drawTicks: true, // empty space between labels and bars
        color: getThemeColor(darkTheme, 'color-border-lower'),
      },
      ticks: {
        display: true,
        padding: TICK_PADDING,
        color: getThemeColor(darkTheme, 'color-text-medium'),
        font: categoryLabelFont,
        callback: getNumberText,
      },
    },
  },
});

export const getChartOptions = (
  darkTheme: boolean,
  histogram: boolean,
  t: TFunction,
  transKey: string,
  numberLocale: string,
  getTooltipTitle: GetTooltipTitle,
  getNumberText: OnGetValue,
  onCategorySelect?: OnCategorySelect,
  onBarSelect?: OnBarSelect,
): ChartOptions<'bar'> =>
  histogram
    ? getChartOptionsHistogram(
        darkTheme,
        t,
        transKey,
        numberLocale,
        getTooltipTitle,
        getNumberText,
        onCategorySelect,
        onBarSelect,
      )
    : getChartOptionsBar(
        darkTheme,
        t,
        transKey,
        numberLocale,
        getTooltipTitle,
        getNumberText,
        onCategorySelect,
        onBarSelect,
      );

const getStackedBarChartHeight = (numBars: number) => {
  const contentHeight = numBars * BAR_SECTION_THICKNESS + BAR_GAP;
  return contentHeight + X_AXIS_HEIGHT + LEGEND_HEIGHT;
};

const getBarChartHeight = (numLabels: number, numDataSets: number) => {
  const labelHeight = numDataSets * BAR_THICKNESS;
  const contentHeight = numLabels * (labelHeight + BAR_GAP) + BAR_GAP;
  return contentHeight + X_AXIS_HEIGHT + LEGEND_HEIGHT;
};

const getHistogramChartHeight = () => HISTOGRAM_CHART_HEIGHT;

export const getChartHeight = (
  numLabels: number,
  numDataSets: number,
  histogram: boolean,
  stacked: boolean,
) => {
  return histogram
    ? getHistogramChartHeight()
    : stacked
      ? getStackedBarChartHeight(numLabels)
      : getBarChartHeight(numLabels, numDataSets);
};

// Keep height consistent with histogram
export const getLineChartHeight = () => HISTOGRAM_CHART_HEIGHT;

export const POINT_HOVER_RADIUS = 5;
const SAFE_PADDING = POINT_HOVER_RADIUS + 1;

export const getSparkChartOptions = (
  darkTheme: boolean,
  timezone: string | undefined,
  numberLocale: string,
  t: TFunction,
): ChartOptions<'line'> => ({
  ...responsiveOptions,
  indexAxis: 'x',
  plugins: {
    title: titleOptions,
    tooltip: {
      ...getTooltipOptions(darkTheme),
      padding: getVdsSpacing(3),
      callbacks: {
        label: (context: TooltipItem<'line'>) =>
          ` ${t('AssetsWithCount', { count: context.parsed.y, formatParams: { count: { lng: numberLocale } } })}`,
      },
    },
    legend: { display: false },
  },
  layout: {
    padding: SAFE_PADDING,
  },
  scales: {
    x: {
      display: false,
      type: 'time',
      time: {
        tooltipFormat: 'LLL dd yyyy, t', // Luxon format
      },
      adapters: {
        date: {
          numberLocale,
          setZone: true,
          zone: timezone,
        },
      },
    },
    y: {
      display: false,
      beginAtZero: true,
    },
  },
  showLine: true,
  clip: SAFE_PADDING,
});

const getTimeSeriesLabel = (
  value: string | number,
  timeCategory: TimeUnit,
  timezone: string | undefined,
  locale: string,
): string => {
  if (typeof value !== 'number') return '';

  const dt = DateTime.fromMillis(value).setZone(timezone).setLocale(locale);

  // Show time if <= 24 hours, otherwise show day and month (not year)
  return timeCategory === TimeUnit.Hours
    ? dt.toLocaleString(DateTime.TIME_SIMPLE)
    : dt.toLocaleString({ ...DateTime.DATE_MED, year: undefined });
};

const getTimeSeriesTooltipFormat = (timeCategory: TimeUnit) =>
  timeCategory === TimeUnit.Hours ? 'ff' : 'DD';

const getTimeRangeFromSelection = (
  timestamps: string[],
  minPercent: number,
  maxPercent: number,
): [DateTime, DateTime] => {
  // Get the range of the chart data in milliseconds.
  const firstTimestamp = timestamps[0];
  const lastTimestamp = timestamps[timestamps.length - 1];
  const firstMillis = DateTime.fromISO(firstTimestamp, {
    zone: 'UTC',
  }).toMillis();
  const lastMillis = DateTime.fromISO(lastTimestamp, {
    zone: 'UTC',
  }).toMillis();
  const dataRangeMillis = lastMillis - firstMillis;

  // Convert the plugin position percentages to epoch millis range.
  const startMillis = firstMillis + Math.floor(dataRangeMillis * minPercent);
  const endMillis = firstMillis + Math.ceil(dataRangeMillis * maxPercent);
  const start = DateTime.fromMillis(startMillis, { zone: 'UTC' });
  const end = DateTime.fromMillis(endMillis, { zone: 'UTC' });

  return [start, end];
};

const getRoundedTimeRange = (
  rangeInterval: Interval<true>,
  searchInterval: Interval,
  bucketSize: DurationLikeObject,
): [DateTime, DateTime] => {
  const buckets: Interval<true>[] = searchInterval.splitBy(bucketSize);
  let nearestStart: DateTime<true> = rangeInterval.start;
  let nearestEnd: DateTime<true> = rangeInterval.end;

  let idx = 0; // Share the index between the for loops to avoid double handling.

  // Search sequentially for the first bucket that contains the start of the time range.
  for (; idx < buckets.length; idx++) {
    const bucket = buckets[idx];
    if (bucket.contains(rangeInterval.start)) {
      nearestStart = bucket.start;
      break;
    }
  }

  // Skip to the start bucket index, and find the first bucket that contains the end of the time range.
  for (; idx < buckets.length; idx++) {
    const bucket = buckets[idx];
    if (bucket.contains(rangeInterval.end)) {
      nearestEnd = bucket.end;
      break;
    }
  }

  return [nearestStart, nearestEnd];
};

const isTimeRangeWithin = (rangeMillis: number, rangeObject: DurationLikeObject): boolean => {
  return rangeMillis <= Duration.fromObject(rangeObject).as('milliseconds');
};

const getSearchInterval = (rangeInterval: Interval<true>, unit: DateTimeUnit): Interval<true> => {
  const searchStart = rangeInterval.start.startOf(unit);
  const searchEnd = rangeInterval.end.plus({ [unit]: 1 }).startOf(unit);
  const searchInterval = Interval.fromDateTimes(searchStart, searchEnd);

  // The search interval is always expected to return, but this condition satisfies the compiler.
  return searchInterval.isValid ? searchInterval : rangeInterval;
};

const getBucketsFromTimeRange = (start: DateTime, end: DateTime): [DateTime, DateTime] => {
  const rangeInterval = Interval.fromDateTimes(start, end);

  // If the interval is invalid, return the original start and end times. (This check is here solely to satisfy the compiler.)
  if (!rangeInterval.isValid) return [start, end];

  const rangeMillis = rangeInterval.toDuration().as('milliseconds');

  let startBucket: DateTime;
  let endBucket: DateTime;

  if (isTimeRangeWithin(rangeMillis, { hours: 1 })) {
    const searchInterval = getSearchInterval(rangeInterval, 'hour');
    [startBucket, endBucket] = getRoundedTimeRange(rangeInterval, searchInterval, { minutes: 5 });
  } else if (isTimeRangeWithin(rangeMillis, { hours: 4 })) {
    const searchInterval = getSearchInterval(rangeInterval, 'hour');
    [startBucket, endBucket] = getRoundedTimeRange(rangeInterval, searchInterval, { minutes: 15 });
  } else if (isTimeRangeWithin(rangeMillis, { days: 2 })) {
    const searchInterval = getSearchInterval(rangeInterval, 'hour');
    [startBucket, endBucket] = getRoundedTimeRange(rangeInterval, searchInterval, { hours: 1 });
  } else if (isTimeRangeWithin(rangeMillis, { days: 4 })) {
    const searchInterval = getSearchInterval(rangeInterval, 'day');
    [startBucket, endBucket] = getRoundedTimeRange(rangeInterval, searchInterval, { hours: 4 });
  }
  // Everything above this range is one value per day.
  else {
    const searchInterval = getSearchInterval(rangeInterval, 'day');
    startBucket = searchInterval.start;
    endBucket = searchInterval.end;
  }

  return [startBucket, endBucket];
};

const getNearestTimeRangeFromSelection = (
  timestamps: string[],
  minPercent: number,
  maxPercent: number,
  timezone: string | undefined,
): CustomTimeRangeParams => {
  // Get the exact UTC time range from the user selection.
  const [start, end] = getTimeRangeFromSelection(timestamps, minPercent, maxPercent);

  // Convert range to the user's API defined timezone.
  const startLocal = start.setZone(timezone);
  const endLocal = end.setZone(timezone);

  // Convert the exact time range to a range that snaps to defined time buckets.
  const [startBucket, endBucket] = getBucketsFromTimeRange(startLocal, endLocal);

  // Return as UTC strings.
  return {
    timeRangeStart: startBucket.toUTC().toString(),
    timeRangeEnd: endBucket.toUTC().toString(),
  };
};

const generateLineChartLabels = (chart: Chart): LegendItem[] => {
  // Apply a filled style to the legend. By default, chartjs will use the area background colour (which we set with opacity)
  const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
  labels.forEach(label => {
    label.fillStyle = label.strokeStyle;
  });
  return labels;
};

export const getLineChartOptions = (
  darkTheme: boolean,
  timezone: string | undefined,
  locale: string,
  timeCategory: TimeUnit,
  timestamps: string[],
  t: TFunction,
  transKey: string,
  enableCustomTimeRange: boolean,
  getNumberText: OnGetValue,
  setCustomTimeRange?: (customTimeRange: CustomTimeRangeParams) => void,
): ChartOptions<'line'> => {
  // There must be a valid callback and at least 2 timestamps to be able to set a custom time range.
  const isCustomRangePluginEnabled =
    enableCustomTimeRange && setCustomTimeRange && timestamps.length >= 2;

  const customTimeRangeCallback = (minPercent: number, maxPercent: number): void => {
    if (isCustomRangePluginEnabled) {
      const customTimeRange = getNearestTimeRangeFromSelection(
        timestamps,
        minPercent,
        maxPercent,
        timezone,
      );
      setCustomTimeRange(customTimeRange);
    }
  };

  return {
    ...responsiveOptions,
    indexAxis: 'x',
    plugins: {
      title: titleOptions,
      tooltip: {
        ...getTooltipOptions(darkTheme),
        padding: getVdsSpacing(3),
        callbacks: {
          label: (context: TooltipItem<'line'>) =>
            ` ${t(transKey, { count: context.parsed.y, formatParams: { count: { lng: locale } } })}`,
          labelColor: (context: TooltipItem<'line'>): TooltipLabelStyle => {
            const { borderColor = 'black' } = context.dataset;
            const color = typeof borderColor === 'string' ? borderColor : 'black';
            // Apply a filled style to the tooltip.
            return { borderColor: color, backgroundColor: color };
          },
        },
      },
      legend: legendOptions(darkTheme, generateLineChartLabels),
      [CUSTOM_RANGE_PLUGIN_ID]: isCustomRangePluginEnabled
        ? {
            ...customRangeOptionsLight,
            callback: customTimeRangeCallback,
          }
        : false,
    },
    layout: {
      padding: SAFE_PADDING,
    },
    scales: {
      x: {
        type: 'time',
        time: {
          tooltipFormat: getTimeSeriesTooltipFormat(timeCategory),
        },
        ticks: {
          source: 'data',
          callback: (value: string | number) =>
            getTimeSeriesLabel(value, timeCategory, timezone, locale),
          font: defaultFont,
        },
        grid: {
          display: false,
        },
        adapters: {
          date: {
            locale,
            setZone: true,
            zone: timezone,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          z: 99,
          drawTicks: true, // empty space between labels and bars
          color: (ctx: ScriptableScaleContext) => hideFirstAxisLine(ctx, darkTheme),
        },
        ticks: {
          display: true,
          precision: 0,
          padding: TICK_PADDING,
          color: getThemeColor(darkTheme, 'color-text-medium'),
          font: categoryLabelFont,
          callback: getNumberText,
        },
        border: {
          display: false,
        },
      },
    },
    showLine: true,
    clip: SAFE_PADDING,
  };
};
